import axios from 'axios';
import { getAuthToken } from '../utils/authUtils';



const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';


/**
 * Get quality metrics for a facility
 * @param {string} program Optional program filter
 * @returns {Promise<Array>} Array of quality metrics
 */
export async function getQualityMetrics(program, facilityId) {
  try {
    console.log('Getting quality metrics via API:', program);
    const token = await getAuthToken();
    
    // Get the user's default facility ID
    if (!facilityId) {
      throw new Error('No facility ID available. Please ensure you have access to a facility.');
    }
    
    const params = new URLSearchParams();
    params.append('facilityId', facilityId);
    if (program && program !== 'All') {
      params.append('program', program);
    }
    
    const response = await axios.get(
      `${API_BASE}/bi/quality-metrics?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.qualityMetrics || [];
  } catch (error) {
    console.error('Error getting quality metrics:', error);
    // Return empty array on error to prevent undefined errors in UI
    return [];
  }
}

/**
 * Get patient-level details for a specific quality metric
 * @param {string} metricId The ID of the metric
 * @returns {Promise<Object>} Patient-level metric details
 */
export async function getQualityMetricPatients(metricId, facilityId) {
  try {
    console.log('Getting patient-level metric details:', metricId);
    const token = await getAuthToken();
    
    if (!facilityId) {
      throw new Error('No facility ID available');
    }
    
    const response = await axios.get(
      `${API_BASE}/bi/quality-metrics/${metricId}/patients`,
      {
        params: { facilityId: facilityId },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting metric patient details:', error);
    throw error;
  }
}

/**
 * Get historical trends for a specific quality metric
 * @param {string} metricId The ID of the metric
 * @param {string} interval Time interval ('month' or 'week')
 * @param {number} period Number of months to look back
 * @returns {Promise<Object>} Trend data and analysis
 */
export async function getQualityMetricTrends(metricId, interval = 'month', period = 6, facilityId) {
  try {
    console.log('Getting metric trends:', metricId);
    const token = await getAuthToken();
    
    if (!facilityId) {
      throw new Error('No facility ID available');
    }
    
    const response = await axios.get(
      `${API_BASE}/bi/quality-metrics/${metricId}/trends`,
      {
        params: {
          facilityId: facilityId,
          interval,
          period
        },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting metric trends:', error);
    throw error;
  }
}

/**
 * Force recalculation of quality metrics
 * NOTE: With BigQuery analytics enabled, this is deprecated as metrics are calculated in real-time
 * @returns {Promise<Object>} Status of the recalculation request
 */
export async function recalculateQualityMetrics(facilityId) {
  try {
    console.log('Requesting metrics recalculation');
    const token = await getAuthToken();
    
    if (!facilityId) {
      throw new Error('No facility ID available');
    }
    
    const response = await axios.post(
      `${API_BASE}/bi/quality-metrics/recalculate`,
      { facilityId: facilityId },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error requesting metrics recalculation:', error);
    throw error;
  }
}

// =============================================================================
// BIGQUERY ANALYTICS ENDPOINTS
// These endpoints provide real-time metrics calculated from BigQuery views
// =============================================================================

/**
 * Get real-time calculated quality metrics from BigQuery
 * These metrics are calculated on-demand from raw data via SQL views
 * @param {string} facilityId Facility ID
 * @param {string} program Optional program filter
 * @returns {Promise<Object>} Calculated quality metrics
 */
export async function getCalculatedQualityMetrics(facilityId, program = null) {
  try {
    console.log('Getting calculated quality metrics from BigQuery:', facilityId);
    const token = await getAuthToken();
    
    if (!facilityId) {
      throw new Error('No facility ID available');
    }
    
    const params = new URLSearchParams();
    params.append('facilityId', facilityId);
    if (program && program !== 'All') {
      params.append('program', program);
    }
    
    const response = await axios.get(
      `${API_BASE}/bi/analytics/quality-metrics/calculated?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting calculated quality metrics:', error);
    throw error;
  }
}

/**
 * Get quality metrics trends from BigQuery with moving averages
 * @param {string} facilityId Facility ID
 * @param {string} metricId Optional metric ID filter
 * @param {number} days Number of days to look back (default 30)
 * @returns {Promise<Object>} Trend data with moving averages
 */
export async function getAnalyticsTrends(facilityId, metricId = null, days = 30) {
  try {
    console.log('Getting analytics trends from BigQuery:', facilityId);
    const token = await getAuthToken();
    
    if (!facilityId) {
      throw new Error('No facility ID available');
    }
    
    const params = new URLSearchParams();
    params.append('facilityId', facilityId);
    params.append('days', days.toString());
    if (metricId) {
      params.append('metricId', metricId);
    }
    
    const response = await axios.get(
      `${API_BASE}/bi/analytics/quality-metrics/trends?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting analytics trends:', error);
    throw error;
  }
}

/**
 * Get program performance summary from BigQuery
 * @param {string} facilityId Facility ID
 * @returns {Promise<Object>} Program summary data
 */
export async function getProgramSummary(facilityId) {
  try {
    console.log('Getting program summary from BigQuery:', facilityId);
    const token = await getAuthToken();
    
    if (!facilityId) {
      throw new Error('No facility ID available');
    }
    
    const response = await axios.get(
      `${API_BASE}/bi/analytics/programs/summary`,
      {
        params: { facilityId },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting program summary:', error);
    throw error;
  }
}

/**
 * Get facility performance dashboard from BigQuery
 * @param {string} facilityId Facility ID
 * @returns {Promise<Object>} Facility dashboard KPIs
 */
export async function getFacilityDashboard(facilityId) {
  try {
    console.log('Getting facility dashboard from BigQuery:', facilityId);
    const token = await getAuthToken();
    
    if (!facilityId) {
      throw new Error('No facility ID available');
    }
    
    const response = await axios.get(
      `${API_BASE}/bi/analytics/facility-dashboard`,
      {
        params: { facilityId },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting facility dashboard:', error);
    throw error;
  }
}

/**
 * Get provider productivity from BigQuery
 * @param {string} facilityId Facility ID
 * @returns {Promise<Object>} Provider productivity data
 */
export async function getProviderProductivity(facilityId) {
  try {
    console.log('Getting provider productivity from BigQuery:', facilityId);
    const token = await getAuthToken();
    
    if (!facilityId) {
      throw new Error('No facility ID available');
    }
    
    const response = await axios.get(
      `${API_BASE}/bi/analytics/providers/productivity`,
      {
        params: { facilityId },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting provider productivity:', error);
    throw error;
  }
}

/**
 * Get daily volume trends from BigQuery
 * @param {string} facilityId Facility ID
 * @param {number} days Number of days to look back (default 30)
 * @returns {Promise<Object>} Daily volume trends
 */
export async function getVolumeTrends(facilityId, days = 30) {
  try {
    console.log('Getting volume trends from BigQuery:', facilityId);
    const token = await getAuthToken();
    
    if (!facilityId) {
      throw new Error('No facility ID available');
    }
    
    const response = await axios.get(
      `${API_BASE}/bi/analytics/volume-trends`,
      {
        params: { facilityId, days },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting volume trends:', error);
    throw error;
  }
}

/**
 * Check analytics health/connection status
 * @returns {Promise<Object>} Analytics health status
 */
export async function getAnalyticsHealth() {
  try {
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/bi/analytics/health`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting analytics health:', error);
    throw error;
  }
}
